import { useMemo } from 'react'
import { sum } from 'lodash'
import Styles from './AddMoreContentModal.module.scss'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Plus1 } from 'blaze-ui/icons/24'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Minus } from 'almanac-ui/icons'
import { ContentType } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { TikTokBrand, TwitterBrand, YouTubeBrand } from 'blaze-ui/icons'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { FacebookBrand, MailchimpBrand, WordPressBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { AddMoreContentProps } from './Types'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { GoHighLevelBrand } from 'blaze-ui/icons/35/GoHighLevelBrand'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { BrandPlanTactic } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import CrossPostIcon from '../../../../../../components/FirstTimeExperienceModal/steps/BrandPlanAwareness/CrossPostIcon'
import cx from 'classnames'
import { useModals } from 'almanac-ui/Modal'
import { useAutopilotLimits } from 'hooks/useAutopilotLimits'
import { AutopilotProPill } from 'components/AutopilotUpgradeButtons'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { calculateChannelCounts, channelTypeToField, getCountFromDirectProperties } from './utils'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { pluralize } from 'utils'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { useNeedsCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'

interface Channel {
  name: string
  type: ContentType
  icon: any
  isCrosspost?: boolean
  providers?: ExternalIntegrationEnum[]
}

const MAX_ADDITIONALS = 50
const MAX_TOTAL = 200
const MAX_ADDITIONAL_PER_CHANNEL = 10
const MAX_TOTAL_PER_CHANNEL = 15

export const AddMoreContent = ({
  onNext,
  targetSchedule,
  additionals,
  setAdditionals,
  onBack,
  backButtonLabel = 'Back',
}: AddMoreContentProps) => {
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { closeModal, openModal } = useModals()
  const isYoutubeEnabled = useFlipperFlag('youtube_integration')
  const isVideoAutoCrosspostingEnabled = useFlipperFlag('video_autocrossposting')
  const { maxChannels, disabledChannels, maxPostsPerChannel } = useAutopilotLimits()
  const workspace = useCurrentWorkspace()
  const { needsCredits, autopilotContentCreditsPrice } = useNeedsCreditsToGenerateContent()

  const channels = useMemo(() => {
    // Determine blog post label and icon based on connected integration
    const isGoHighLevelConnected = connectedBlogIntegration?.provider === ExternalIntegrationEnum.GoHighLevel
    const blogPostName = isGoHighLevelConnected ? 'GoHighLevel Blog Posts' : 'Wordpress Blog Posts'
    const blogPostIcon = isGoHighLevelConnected ? GoHighLevelBrand : WordPressBrand

    // Get enabled tactics from targetSchedule
    const enabledTactics = targetSchedule?.tactics || []

    // Create crosspost providers array based on enabled tactics
    const crosspostProviders = [
      enabledTactics.includes(BrandPlanTactic.Instagram) && ExternalIntegrationEnum.Instagram,
      enabledTactics.includes(BrandPlanTactic.Facebook) && ExternalIntegrationEnum.Facebook,
      enabledTactics.includes(BrandPlanTactic.Twitter) && ExternalIntegrationEnum.X,
      enabledTactics.includes(BrandPlanTactic.LinkedIn) && ExternalIntegrationEnum.LinkedIn,
    ].filter(Boolean) as ExternalIntegrationEnum[]

    // Create video crosspost providers array based on enabled tactics
    const videoCrosspostProviders = [
      enabledTactics.includes(BrandPlanTactic.Instagram) && ExternalIntegrationEnum.Instagram,
      enabledTactics.includes(BrandPlanTactic.YouTube) && ExternalIntegrationEnum.YouTube,
      enabledTactics.includes(BrandPlanTactic.TikTok) && ExternalIntegrationEnum.TikTok,
    ].filter(Boolean) as ExternalIntegrationEnum[]

    return [
      ...(crosspostProviders.length > 0
        ? [
            {
              name: 'Cross Posts',
              type: 'crosspost_post',
              icon: CrossPostIcon,
              isCrosspost: true,
              providers: crosspostProviders,
            },
          ]
        : []),
      ...(isVideoAutoCrosspostingEnabled && videoCrosspostProviders.length > 0
        ? [
            {
              name: 'Video Cross Posts',
              type: 'video_crosspost_post',
              icon: CrossPostIcon,
              isCrosspost: true,
              providers: videoCrosspostProviders,
            },
          ]
        : []),
      { name: 'Instagram Posts', type: 'instagram_post', icon: InstagramBrand },
      { name: 'Instagram Stories', type: 'instagram_story', icon: InstagramBrand },
      { name: 'Instagram Reels', type: 'instagram_reel', icon: InstagramBrand },
      ...(isYoutubeEnabled ? [{ name: 'YouTube Shorts', type: 'youtube_short', icon: YouTubeBrand }] : []),
      { name: 'TikTok Posts', type: 'tiktok_post', icon: TikTokBrand },
      { name: 'Facebook Posts', type: 'facebook_post', icon: FacebookBrand },
      { name: 'LinkedIn Posts', type: 'linkedin_post', icon: LinkedInBrand },
      { name: 'X/Twitter Posts', type: 'twitter_post', icon: TwitterBrand },
      { name: blogPostName, type: 'blog_post', icon: blogPostIcon },
      { name: 'Emails', type: 'newsletter', icon: MailchimpBrand },
    ] as Channel[]
  }, [connectedBlogIntegration?.provider, isYoutubeEnabled, isVideoAutoCrosspostingEnabled, targetSchedule?.tactics])

  const channelCounts = useMemo(() => {
    return calculateChannelCounts(targetSchedule)
  }, [targetSchedule])

  const usedChannels = useMemo(() => {
    return channels
      .filter((channel) => {
        // First check channelCounts (from contents array), if not available, check direct properties
        const currentCount =
          channelCounts?.[channel.type] !== undefined
            ? channelCounts[channel.type]
            : getCountFromDirectProperties(channel.type, targetSchedule)
        const additionalCount = additionals[channel.type] || 0
        return currentCount + additionalCount > 0
      })
      .map((channel) => channel.type)
  }, [channels, channelCounts, additionals, targetSchedule])

  const channelsCount = useMemo(() => {
    return sum(Object.values(channelCounts || {}))
  }, [channelCounts])

  const additionalsCount = useMemo(() => {
    return sum(Object.values(additionals))
  }, [additionals])

  const totalCount = useMemo(() => {
    return additionalsCount + channelsCount
  }, [additionalsCount, channelsCount])

  const maxTotal = Math.min(MAX_TOTAL, maxPostsPerChannel)
  const showLimits = maxTotal === maxPostsPerChannel

  const exceedsMax = additionalsCount >= MAX_ADDITIONALS || totalCount >= MAX_TOTAL

  const handleRemoveAdditional = (type: ContentType) => {
    setAdditionals((prev) => ({ ...prev, [type]: Math.max(prev[type] - 1, 0) }))
  }

  const handleAddAdditional = (type: ContentType) => {
    setAdditionals((prev) => ({ ...prev, [type]: prev[type] + 1 }))
  }

  const handleAddContent = () => {
    if (needsCredits && autopilotContentCreditsPrice) {
      openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice, onSuccess: onNext })
      return
    }

    onNext()
  }

  return (
    <>
      <div className={Styles.addMoreContentHeaderContainer}>
        <div className={Styles.addMoreContentHeader}>
          {onBack && <Modal.BackButton onPress={onBack} />}
          <Heading level={3}>Add More Content</Heading>
        </div>
      </div>
      <Modal.Content>
        <div className={Styles.contentContainer}>
          <Heading level={5}>Pick what you want Blaze to create for you.</Heading>
          {workspace?.is_ap_freemium && (
            <Text variant="secondary">{`On your Free plan, you can auto-generate up to ${maxPostsPerChannel} posts across ${maxChannels} channels (up to ${
              maxPostsPerChannel * maxChannels
            } posts total).`}</Text>
          )}
          <div className={Styles.channels}>
            <div className={Styles.header}>
              <Text variant="label">Type</Text>
              <div className={Styles.addHeader}>
                {workspace?.is_ap_freemium && <Text variant="label">Scheduled</Text>}
                <Text variant="label">Add</Text>
              </div>
            </div>
            <div className={Styles.body}>
              {channels.map((channel, index) => {
                const now =
                  channelCounts?.[channel.type] !== undefined
                    ? channelCounts[channel.type]
                    : getCountFromDirectProperties(channel.type, targetSchedule)
                const additional = additionals[channel.type] || 0
                const total = now + additional
                const isProOnly = disabledChannels.includes(channelTypeToField[channel.type])
                const disabledFromChannelLimit =
                  maxChannels <= usedChannels.length && !usedChannels.includes(channel.type)
                const disabled = isProOnly || disabledFromChannelLimit
                const showLockButtonForChannel = showLimits && total >= maxPostsPerChannel

                let lockedTooltipMessage = ''
                if (isProOnly) {
                  lockedTooltipMessage = 'Upgrade to enable cross posts'
                } else if (showLockButtonForChannel) {
                  lockedTooltipMessage = `Upgrade to enable more than ${maxPostsPerChannel} ${pluralize(
                    'post',
                    maxPostsPerChannel,
                  )}/week`
                } else if (disabledFromChannelLimit) {
                  lockedTooltipMessage = `To enable this channel, remove another first`
                }

                return (
                  <div key={`${channel.type}-${index}`} className={Styles.row}>
                    <div className={Styles.channelNameContainer}>
                      <div className={cx(Styles.channelName, { [Styles.disabledContent]: disabled })}>
                        {channel.isCrosspost ? (
                          <channel.icon providers={channel.providers} />
                        ) : (
                          <channel.icon size={20} />
                        )}
                        <Heading level={5}>{channel.name}</Heading>
                      </div>
                      {isProOnly && (
                        <Tooltip placement="top" label="Upgrade to enable cross posts">
                          <div>
                            <AutopilotProPill />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                    <div className={Styles.count}>
                      {workspace?.is_ap_freemium && !channel.isCrosspost && (
                        <div className={Styles.currentCount}>
                          <Text variant="placeholder">{now}</Text>
                        </div>
                      )}
                      <div className={Styles.additional}>
                        <IconButton
                          icon={Minus}
                          size="sm"
                          variant="ghost-tertiary"
                          isDisabled={disabled}
                          onPress={() => handleRemoveAdditional(channel.type)}
                        />
                        <div
                          className={cx(Styles.additionalNumber, {
                            [Styles.hasAdditional]: additional > 0,
                            [Styles.disabledContent]: disabled,
                          })}
                        >
                          {additional}
                        </div>
                        <Tooltip label={lockedTooltipMessage} placement="top" hide={!lockedTooltipMessage}>
                          <div>
                            <IconButton
                              icon={Plus1}
                              size="sm"
                              variant="ghost-tertiary"
                              isDisabled={
                                disabled ||
                                additional >= MAX_ADDITIONAL_PER_CHANNEL ||
                                total >= MAX_TOTAL_PER_CHANNEL ||
                                exceedsMax ||
                                showLockButtonForChannel
                              }
                              onPress={() => handleAddAdditional(channel.type)}
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={onBack || closeModal}>
            {onBack ? backButtonLabel : 'Cancel'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            onPress={handleAddContent}
            size="lg"
            isDisabled={sum(Object.values(additionals)) === 0 && !needsCredits}
          >
            {needsCredits ? 'Add More Credits' : 'Add Topic & Media'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
